import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  InfoOutlined as InfoIcon,
  ContactMailOutlined as ContactIcon,
  PsychologyOutlined as EmbeddingIcon,
  PaletteOutlined as BrandingIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { ROUTES } from '@/routes/config';
import { FormProvider, TextBox, SelectBox, ColorPicker } from '@/components/form';
import PageLayout from '@/components/PageLayout';
import { usePortals } from './hooks/usePortals';
import { createPortalSchema, type CreatePortalFormValues } from './validations';
import { useMasterLlmModels } from '@/containers/PortalDetails/hooks/useMasterLlmModels';

type SectionKey = 'portal' | 'contact' | 'embedding' | 'branding';

interface SectionEntry {
  key: SectionKey;
  label: string;
  description: string;
  icon: React.ReactElement;
}

interface SectionGroup {
  title: string;
  items: SectionEntry[];
}

const SECTION_GROUPS: SectionGroup[] = [
  {
    title: 'GENERAL',
    items: [
      {
        key: 'portal',
        label: 'Portal Information',
        description: 'Name & slug',
        icon: <InfoIcon fontSize="small" />,
      },
      {
        key: 'contact',
        label: 'Contact Information',
        description: 'Owner & contact details',
        icon: <ContactIcon fontSize="small" />,
      },
    ],
  },
  {
    title: 'AI & APPEARANCE',
    items: [
      {
        key: 'embedding',
        label: 'Embedding Configuration',
        description: 'Provider & model',
        icon: <EmbeddingIcon fontSize="small" />,
      },
      {
        key: 'branding',
        label: 'Branding',
        description: 'Colors & theme',
        icon: <BrandingIcon fontSize="small" />,
      },
    ],
  },
];

const SECTIONS: SectionEntry[] = SECTION_GROUPS.flatMap((g) => g.items);

const FIELDS_BY_SECTION: Record<SectionKey, (keyof CreatePortalFormValues)[]> = {
  portal: ['name'],
  contact: ['contact_name', 'contact_email', 'contact_phone'],
  embedding: ['embedding_provider', 'embedding_model'],
  branding: ['primary_color', 'secondary_color'],
};

const CreatePortal: React.FC = () => {
  const navigate = useNavigate();
  const { createPortalAsync, isCreating } = usePortals();
  const { embeddingModels, isLoading: isLoadingModels } = useMasterLlmModels();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<SectionKey>('portal');

  const isSubmitting = isCreating || isSaving;

  const methods = useForm<CreatePortalFormValues>({
    resolver: yupResolver(createPortalSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      embedding_provider: '',
      embedding_model: '',
      primary_color: '',
      secondary_color: '',
    },
  });

  const { errors, isValid } = methods.formState;
  const errorCountBySection = useMemo(() => {
    const map: Record<SectionKey, number> = {
      portal: 0,
      contact: 0,
      embedding: 0,
      branding: 0,
    };
    (Object.keys(FIELDS_BY_SECTION) as SectionKey[]).forEach((section) => {
      map[section] = FIELDS_BY_SECTION[section].filter(
        (field) => Boolean(errors[field]),
      ).length;
    });
    return map;
  }, [errors]);

  const handleInvalid = () => {
    const firstInvalid = SECTIONS.find(
      (s) => errorCountBySection[s.key] > 0,
    );
    if (firstInvalid) setActiveSection(firstInvalid.key);
  };

  const nameValue = methods.watch('name');
  const selectedProvider = methods.watch('embedding_provider');
  const embeddingProviderOptions = useMemo(() => {
    const providers = [...new Set(embeddingModels.map((m) => m.provider))].sort();
    return providers.map((p) => ({ label: p, value: p }));
  }, [embeddingModels]);

  const prevProviderRef = useRef(selectedProvider);
  useEffect(() => {
    if (prevProviderRef.current !== selectedProvider) {
      methods.setValue('embedding_model', '');
      prevProviderRef.current = selectedProvider;
    }
  }, [selectedProvider, methods]);

  const embeddingModelOptions = useMemo(() => {
    if (!selectedProvider) return [];
    return embeddingModels
      .filter((m) => m.provider === selectedProvider)
      .map((m) => ({ label: m.model_name, value: m.model_id }));
  }, [embeddingModels, selectedProvider]);

  const slug = useMemo(
    () =>
      (nameValue || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80),
    [nameValue],
  );

  const onSubmit = async (data: CreatePortalFormValues) => {
    setError(null);
    try {
      setIsSaving(true);

      await createPortalAsync({
        slug,
        name: data.name.trim(),
        ...(data.contact_name?.trim() && { contact_name: data.contact_name.trim() }),
        ...(data.contact_email?.trim() && { contact_email: data.contact_email.trim() }),
        ...(data.contact_phone?.trim() && { contact_phone: data.contact_phone.trim() }),
        settings: {
          embedding_provider: data.embedding_provider,
          embedding_model: data.embedding_model,
        },
        ...((data.primary_color?.trim() || data.secondary_color?.trim()) && {
          branding: {
            ...(data.primary_color?.trim() && { primary_color: data.primary_color.trim() }),
            ...(data.secondary_color?.trim() && { secondary_color: data.secondary_color.trim() }),
          },
        }),
      });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; detail?: string } }; message?: string };
      setError(error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Failed to create portal');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageLayout
      title="Create Portal"
      subtitle="Set up a new portal for your organization."
      backTo={ROUTES.PORTALS}
    >
      <FormProvider methods={methods} onSubmit={onSubmit} onInvalid={handleInvalid}>
        <Stack spacing={2.5}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '260px 1fr' },
              gap: 3,
              alignItems: 'start',
            }}
          >
            <CreatePortalSideNav
              activeSection={activeSection}
              onSelect={setActiveSection}
              errorCountBySection={errorCountBySection}
            />

            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                minWidth: 0,
              }}
            >
              <Box sx={{ display: activeSection === 'portal' ? 'block' : 'none' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  Portal Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextBox
                      name="name"
                      label="Portal name"
                      placeholder="e.g. Acme Corporation"
                      fullWidth
                      required
                      slotProps={{ htmlInput: { maxLength: 200 } }}
                      helperText={`${(nameValue || '').length}/200 characters`}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Slug"
                      value={slug || 'auto-generated'}
                      fullWidth
                      slotProps={{ input: { readOnly: true } }}
                      helperText={`Auto-generated from name (${slug.length}/80 characters)`}
                      disabled
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ display: activeSection === 'contact' ? 'block' : 'none' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  Contact Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextBox
                      name="contact_name"
                      label="Contact name"
                      placeholder="e.g. John Doe"
                      fullWidth
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextBox
                      name="contact_email"
                      label="Contact email"
                      placeholder="e.g. john@acme.com"
                      fullWidth
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextBox
                      name="contact_phone"
                      label="Contact phone"
                      placeholder="e.g. +1 555-123-4567"
                      fullWidth
                      disabled={isSubmitting}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ display: activeSection === 'embedding' ? 'block' : 'none' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  Embedding Configuration
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <SelectBox
                      name="embedding_provider"
                      label="Embedding provider"
                      fullWidth
                      disabled={isSubmitting || isLoadingModels}
                      options={embeddingProviderOptions}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <SelectBox
                      name="embedding_model"
                      label="Embedding model"
                      fullWidth
                      required
                      disabled={isSubmitting || isLoadingModels || !selectedProvider}
                      options={embeddingModelOptions}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ display: activeSection === 'branding' ? 'block' : 'none' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  Branding
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <ColorPicker
                      name="primary_color"
                      label="Primary color"
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <ColorPicker
                      name="secondary_color"
                      label="Secondary color"
                      disabled={isSubmitting}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            {!isValid && (
              <Typography variant="caption" color="text.secondary">
                Complete required fields to enable create.
              </Typography>
            )}
            <Button
              variant="outlined"
              onClick={() => navigate(ROUTES.PORTALS)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || !isValid}
              startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {isSubmitting ? 'Creating...' : 'Create Portal'}
            </Button>
          </Box>
        </Stack>
      </FormProvider>
    </PageLayout>
  );
};

interface CreatePortalSideNavProps {
  activeSection: SectionKey;
  onSelect: (key: SectionKey) => void;
  errorCountBySection: Record<SectionKey, number>;
}

const CreatePortalSideNav: React.FC<CreatePortalSideNavProps> = ({
  activeSection,
  onSelect,
  errorCountBySection,
}) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  return (
    <Paper
      elevation={0}
      sx={{
        p: 0.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        '& .MuiListItemButton-root': { my: 0, mx: 0.5 },
      }}
    >
      {SECTION_GROUPS.map((group) => {
        const isOpen = !collapsed[group.title];
        const hasActive = group.items.some((i) => i.key === activeSection);
        return (
          <Box key={group.title} sx={{ mb: 0.25 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 2,
                pt: 0.75,
                pb: 0.25,
                cursor: 'pointer',
                userSelect: 'none',
              }}
              onClick={() =>
                setCollapsed((prev) => ({
                  ...prev,
                  [group.title]: !prev[group.title],
                }))
              }
            >
              <Typography
                variant="overline"
                sx={{
                  color: hasActive ? 'text.primary' : 'text.secondary',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  lineHeight: 1,
                }}
              >
                {group.title}
              </Typography>
              <IconButton
                size="small"
                sx={{
                  p: 0.25,
                  transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 0.15s',
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List disablePadding>
                {group.items.map((item) => {
                  const selected = activeSection === item.key;
                  const errCount = errorCountBySection[item.key] ?? 0;
                  return (
                    <ListItemButton
                      key={item.key}
                      selected={selected}
                      onClick={() => onSelect(item.key)}
                      sx={{ py: 0.5, alignItems: 'flex-start' }}
                    >
                      <ListItemIcon sx={{ minWidth: 30, mt: 0.25 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <span>{item.label}</span>
                            {errCount > 0 && (
                              <Badge
                                badgeContent={errCount}
                                color="error"
                                sx={{
                                  '& .MuiBadge-badge': {
                                    position: 'static',
                                    transform: 'none',
                                    fontSize: '0.65rem',
                                    height: 16,
                                    minWidth: 16,
                                  },
                                }}
                              />
                            )}
                          </Stack>
                        }
                        secondary={item.description}
                        primaryTypographyProps={{
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          lineHeight: 1.15,
                        }}
                        secondaryTypographyProps={{
                          fontSize: '0.7rem',
                          color: 'text.secondary',
                          sx: { mt: 0, lineHeight: 1.2 },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        );
      })}
    </Paper>
  );
};

export default CreatePortal;
